/**
 * ApproveWithAccountModal — Modal compartido para aprobar/contabilizar un
 * documento permitiendo elegir opcionalmente la cuenta contable de gasto.
 *
 * Uso: SupplierInvoice approve, InboundDTE import, IntlInvoice post.
 *
 * Si el usuario no selecciona cuenta, se aplican las reglas por defecto
 * (producto → supplier default → AccountingDefaults).
 */
import { useMemo, useState } from 'react'

import { Autocomplete, type AutocompleteItem } from '@/components/Autocomplete'
import { ModalCancelButton } from '@/components/ModalActions'
import { Modal } from '@/components/ds/Modal'
import { inputCls, labelCls } from '@/lib/formStyles'
import { useAccounts } from '@/modules/accounting/hooks/useAccounting'

interface Props {
  open: boolean
  onClose: () => void
  onConfirm: (accountId: string | undefined) => void
  isPending?: boolean
  title?: string
  description?: string
  confirmLabel?: string
}

export function ApproveWithAccountModal({
  open,
  onClose,
  onConfirm,
  isPending = false,
  title = 'Aprobar y contabilizar',
  description = 'Se generará un comprobante contable en borrador con la cuenta de gasto por defecto. Opcionalmente se puede elegir otra cuenta.',
  confirmLabel = 'Aprobar',
}: Props) {
  const [accountId, setAccountId] = useState<string>('')
  const { data } = useAccounts({ is_active: 'true', is_imputable: 'true' })

  const items = useMemo(() => {
    const list = data?.accounts ?? []
    return list
      .filter(a => ['expense', 'asset'].includes(a.account_type))
      .map(a => ({ id: a.id, label: `${a.code} — ${a.name}` }))
  }, [data])

  const handleConfirm = () => {
    onConfirm(accountId || undefined)
  }

  const handleClose = () => {
    setAccountId('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="sm"
      actions={
        <>
          <ModalCancelButton onClick={handleClose} />
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-on-primary bg-primary hover:bg-primary-600 rounded-lg disabled:opacity-60"
          >
            {isPending ? 'Procesando…' : confirmLabel}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-content-muted">{description}</p>
        <div>
          <label htmlFor="approvewithaccountmodal-cuenta-de-gasto-opcional" className={labelCls}>Cuenta de gasto (opcional)</label>
          <Autocomplete id="approvewithaccountmodal-cuenta-de-gasto-opcional"
            items={items}
            value={accountId}
            displayValue={items.find(i => i.id === accountId)?.label ?? ''}
            onChange={() => { /* selección via onSelect */ }}
            onSelect={(item: AutocompleteItem) => setAccountId(item.id)}
            placeholder="Usar cuenta por defecto…"
            className={inputCls}
          />
          {accountId && (
            <button
              type="button"
              onClick={() => setAccountId('')}
              className="mt-1 text-xs text-content-muted hover:text-content underline"
            >
              Limpiar (volver a default)
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}

